/**
 * Global audio "ducking" bus.
 *
 * When the Archivist is speaking (case-file narration, the Subscribe-page
 * invitation, or any future voice), the background music (CodexTurntable)
 * should gently lower its volume so the voice sits on top — then swell back
 * when the voice stops. This tiny pub/sub decouples the many voice sources
 * from the single music player without threading props/context everywhere.
 *
 * Sources call `duckWhilePlaying(audioEl)` on their <audio>/Audio element.
 * The music player calls `subscribeDucking(fn)` and lowers its gain while
 * `active > 0` (ref-counted so overlapping clips are handled correctly).
 */

let active = 0;
const listeners = new Set();

const emit = () => {
  const on = active > 0;
  listeners.forEach((fn) => {
    try { fn(on); } catch (e) { /* ignore listener errors */ }
  });
};

/** Subscribe to duck state changes. Returns an unsubscribe function. */
export function subscribeDucking(fn) {
  listeners.add(fn);
  fn(active > 0); // sync current state immediately
  return () => listeners.delete(fn);
}

/** True while at least one Archivist voice source is playing. */
export function isDucking() {
  return active > 0;
}

/**
 * Bind an <audio>/Audio element so the background music ducks while it plays.
 * Self-balancing via a per-element flag (a source can play/pause repeatedly).
 * Returns an unbind function. Safe to call once per element.
 */
export function duckWhilePlaying(el) {
  if (!el || el.__codexDuckBound) return () => {};
  el.__codexDuckBound = true;
  let counted = false;

  const on = () => {
    if (!counted && !el.paused) { counted = true; active += 1; emit(); }
  };
  const off = () => {
    if (counted) { counted = false; active = Math.max(0, active - 1); emit(); }
  };

  const playEvents = ["play", "playing"];
  const stopEvents = ["pause", "ended", "emptied", "error", "abort"];
  playEvents.forEach((e) => el.addEventListener(e, on));
  stopEvents.forEach((e) => el.addEventListener(e, off));

  return () => {
    playEvents.forEach((e) => el.removeEventListener(e, on));
    stopEvents.forEach((e) => el.removeEventListener(e, off));
    off();
    el.__codexDuckBound = false;
  };
}
